import { CalendarTaskSync } from '@griply/types';
import React, { useMemo } from 'react';
import { FilterMinimalStyleCircle16, IconFormIcons } from 'shared/assets/icons';
import { ActionTaskContextMenu } from 'shared/components/connected/action-task-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { ID } from 'shared/types/id';
import { SmartList } from 'shared/types/smart-list';
import { Task } from 'shared/types/task';

import * as Styled from './event.style';
import { EventCard } from './event-card';
import { TaskEventSyncBadge } from './task-event-sync-badge';

export type TaskSmartListEventProps = {
  task: Task;
  smartLists: SmartList[];
  onClick?: (id: ID) => void;
  numberOfLines: number;
  timeText?: string;
  isPast?: boolean;
  completed?: boolean;
  onInteractionComplete?: () => void;
  calendarSync?: CalendarTaskSync;
};

// Acceptable parallel of TaskLifeAreaEvent/TaskTagEvent (reuse-over-reinvent.md):
// one calendar grid card per timeslot entity; they share the EventCard shape but
// differ on the leading icon and color source.
export const TaskSmartListEvent: React.FC<TaskSmartListEventProps> = ({
  task,
  smartLists,
  onClick,
  numberOfLines,
  timeText,
  isPast,
  completed,
  onInteractionComplete,
  calendarSync,
}) => {
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();
  const _onClick = () => onClick?.(task.id);

  const smartList = useMemo(
    () => smartLists.find(({ id }) => id === task.action?.meta.id),
    [smartLists, task.action?.meta.id],
  );

  if (!smartList) {
    return null;
  }

  return (
    <>
      <EventCard
        title={smartList.name}
        color={smartList.color ?? undefined}
        timeText={timeText}
        completed={completed}
        isPast={isPast}
        numberOfLines={numberOfLines}
        onClick={_onClick}
        onContextMenu={openMenu}
        trailing={
          <TaskEventSyncBadge
            task={task}
            calendarSync={calendarSync}
            color={smartList.color ?? undefined}
          />
        }
        leading={
          <Styled.LeadingSlot
            $color={smartList.color ?? undefined}
            $past={isPast}
          >
            <Icon
              icon={
                smartList.icon
                  ? IconFormIcons[smartList.icon]
                  : FilterMinimalStyleCircle16
              }
            />
          </Styled.LeadingSlot>
        }
      />

      {!!menuPosition && (
        <ActionTaskContextMenu
          task={task}
          onClose={closeMenu}
          location={menuPosition}
          onAction={onInteractionComplete}
        />
      )}
    </>
  );
};
